import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Search, X } from 'lucide-react';

/**
 * SupplierAutocomplete — type-to-search picker for staff visit flows.
 *
 * Props:
 *   options: [{ id, name, address?, has_location?, can_edit_location? }]
 *   value: id (string) | null
 *   onChange(id)
 *   placeholder
 *   testId
 *   excludeIds: string[]   — ids to hide from the list (already-picked)
 *   showGpsPill: boolean   — show small red/green GPS pill before name
 */
export default function SupplierAutocomplete({
  options = [],
  value,
  onChange,
  placeholder = 'Type to search supplier…',
  testId = 'supplier-autocomplete',
  excludeIds = [],
  showGpsPill = true,
}) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  // If parent sets value externally, mirror name into input
  useEffect(() => {
    if (value) {
      const sel = options.find(o => o.id === value);
      setQuery(sel ? sel.name : '');
    } else {
      setQuery('');
    }
  }, [value, options]);

  // Close on outside click
  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    let pool = options.filter(o => !excludeIds.includes(o.id));
    if (q) {
      pool = pool.filter(o =>
        (o.name || '').toLowerCase().includes(q) ||
        (o.address || '').toLowerCase().includes(q)
      );
    }
    // Cap to 30 — keeps the dropdown snappy on a 1200+ supplier list.
    return pool.slice(0, 30);
  }, [options, query, excludeIds]);

  const pick = (opt) => {
    onChange?.(opt.id);
    setQuery(opt.name);
    setOpen(false);
  };

  const clear = () => {
    onChange?.('');
    setQuery('');
    inputRef.current?.focus();
  };

  const onKey = (e) => {
    if (!open && (e.key === 'ArrowDown' || e.key === 'Enter')) {
      setOpen(true); return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight(h => Math.min(h + 1, visible.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight(h => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (visible[highlight]) pick(visible[highlight]);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <div ref={wrapRef} className="relative" data-testid={testId}>
      <div className="relative">
        <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); setHighlight(0); if (value) onChange?.(''); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKey}
          placeholder={placeholder}
          className="w-full pl-8 pr-8 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          data-testid={`${testId}-input`}
          autoComplete="off"
        />
        {query && (
          <button
            type="button"
            onClick={clear}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 text-gray-400 hover:text-gray-700"
            data-testid={`${testId}-clear`}
            aria-label="Clear"
          >
            <X size={14} />
          </button>
        )}
      </div>

      {open && visible.length > 0 && (
        <ul
          className="absolute z-30 left-0 right-0 mt-1 max-h-64 overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg"
          data-testid={`${testId}-dropdown`}
        >
          {visible.map((o, i) => {
            const hasLoc = !!o.has_location;
            return (
              <li
                key={o.id}
                onMouseDown={(e) => { e.preventDefault(); pick(o); }}
                onMouseEnter={() => setHighlight(i)}
                className={`px-3 py-2 text-sm cursor-pointer ${i === highlight ? 'bg-blue-50' : ''}`}
                data-testid={`${testId}-option-${o.id}`}
              >
                <div className="flex items-center gap-1.5">
                  {showGpsPill && (
                    <span
                      className={`inline-flex items-center gap-1 text-[9px] font-semibold px-1.5 py-0.5 rounded-full ${
                        hasLoc ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
                      }`}
                      title={hasLoc ? 'Location saved' : 'Location not set'}
                    >
                      <span className={`w-1 h-1 rounded-full ${hasLoc ? 'bg-emerald-500' : 'bg-red-500'}`} />
                      GPS
                    </span>
                  )}
                  <span className="font-medium truncate">{o.name}</span>
                </div>
                {o.address && (
                  <p className="text-[11px] text-gray-500 truncate mt-0.5 ml-1">{o.address}</p>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {open && visible.length === 0 && query.trim() && (
        <div
          className="absolute z-30 left-0 right-0 mt-1 px-3 py-2 bg-white border border-gray-200 rounded-lg shadow text-xs text-gray-500"
          data-testid={`${testId}-empty`}
        >
          No supplier matches "{query}".
        </div>
      )}
    </div>
  );
}
